import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, map, Observable, of, tap } from 'rxjs';

import { NotificationsService } from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import {
	NotificationStoreData,
	MessageCenterNotificationsListType
} from '@shure/cloud/shared/notifications/shared/models/notification-models';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import {
	PRISM_DIALOG_DATA,
	PrismButtonComponent,
	PrismDialogComponent,
	PrismDialogRef,
	PrismSnackbarService
} from '@shure/prism-angular-components';
import { getConvertedDateTime, updateDateFormat } from '@shure/shared/angular/ui/utils/dates';

@Component({
	selector: 'sh-notification-details-main',
	templateUrl: './notification-details-main.component.html',
	styleUrl: './notification-details-main.component.scss',
	imports: [
		RouterModule,
		FormsModule,
		ReactiveFormsModule,
		PrismDialogComponent,
		TranslocoModule,
		PrismButtonComponent
	],
	changeDetection: ChangeDetectionStrategy.Default
})
export class NotificationDetailsMainComponent implements OnInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);

	public environment: AppEnvironment;
	public isListPage = false;
	public isMarkedRead = false;
	public notificationListType = '';
	public notificationData!: NotificationStoreData;
	public notifications$: Observable<NotificationStoreData[] | null> = EMPTY;
	public formatTime;
	public timeZone!: string | undefined;
	public displayDateFormat!: string;
	protected readonly messageCenterNotificationsListType = MessageCenterNotificationsListType;

	constructor(
		private dialogRef: PrismDialogRef<NotificationDetailsMainComponent>,
		private notificationService: NotificationsService,
		private notificationsStore: NotificationsStore,
		public snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService,
		private organizationStore: OrganizationsStoreService,
		@Inject(APP_ENVIRONMENT) appEnv: AppEnvironment,
		@Inject(PRISM_DIALOG_DATA)
		public data: {
			notificationData: NotificationStoreData;
			isListPage: boolean;
			shouldMarkAsReadOnOpen: boolean;
			notificationListType: string;
		}
	) {
		this.environment = appEnv;
		this.formatTime = getConvertedDateTime;
	}

	/**
	 * Initializes component and loads notification details
	 */
	public ngOnInit(): void {
		this.notificationData = this.data.notificationData;
		this.notificationListType = this.data.notificationListType;
		const accountInfo = this.organizationStore.accountInformation();
		this.timeZone = accountInfo.preferences?.timeZone;
		this.displayDateFormat = accountInfo.preferences?.dateFormat
			? updateDateFormat(accountInfo.preferences?.dateFormat)
			: updateDateFormat('mm/dd/yyyy');
		this.isListPage = this.data.isListPage;
		if (!this.notificationData.readAt) {
			this.markAsRead(this.notificationData.id);
		}
	}

	/**
	 * Closes the notification details dialog without performing any action.
	 */
	public closePopup(): void {
		this.dialogRef.close();
	}

	//TODO:BSR To be uncommented when we utilise markAsArchive functionality
	/**
	 * Archives the selected notification and updates the notification store.
	 * Closes the details dialog after a successful archive operation.
	 *
	 * @param selectedId Unique identifier of the notification to archive.
	 */
	public markAsArchive(selectedId: string): void {
		const requestData = {
			application: this.environment.application as string,
			body: [
				{
					id: selectedId,
					isArchived: true
				}
			]
		};

		const updatedNotifications = [
			{
				id: selectedId,
				isArchived: true
			}
		];

		this.notificationService
			.markAsArchive(requestData, updatedNotifications)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => {
					this.dialogRef.close();
				}),
				catchError(() => {
					this.snackBarService.open({
						message: this.translocoService.translate('cloud.shared.notification.unable-to-archive'),
						actionText: this.translocoService.translate('common.snackbar.dismiss')
					});
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Marks the selected notification as read.
	 *
	 * @param selectedId Unique ID of the notification to mark as read.
	 */
	public markAsRead(selectedId: string): void {
		const data = {
			id: selectedId,
			isRead: true,
			isTrashed: this.notificationListType === this.messageCenterNotificationsListType.TRASH
		};

		const requestData = {
			application: this.environment.application as string,
			body: [data]
		};

		this.notificationService
			.markAsRead(requestData)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => {
					this.isMarkedRead = true;
				}),
				catchError(() => {
					this.snackBarService.open({
						message: this.translocoService.translate('cloud.shared.notification.unable-to-mark-as-read'),
						actionText: this.translocoService.translate('common.snackbar.dismiss')
					});
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Closes the dialog after triggering notification deletion handling.
	 * Actual deletion logic is managed by the parent component.
	 */
	public deleteNotification(): void {
		this.dialogRef.close();
	}

	/**
	 * Moves the selected notification to the trash folder.
	 * Updates the notification store and closes the dialog with the operation result.
	 *
	 * @param selectedId Unique identifier of the notification.
	 */
	public markAsTrash(selectedId: string): void {
		const data = {
			id: selectedId,
			isRead: true,
			isTrashed: true
		};
		const requestData = {
			application: this.environment.application as string,
			body: [data]
		};

		this.notificationService
			.markAsTrash(requestData, {
				...this.notificationData,
				trashedAt: this.notificationService.getCurrentDateInUTC()
			})
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((): { success: boolean } => ({ success: true })),
				catchError(() => of({ success: false })),
				tap((result: { success: boolean }) => {
					this.dialogRef.close(result);
				})
			)
			.subscribe();
	}

	/**
	 * Releases component resources and unsubscribes active streams.
	 * Updates the notification store with the latest read state before disposal.
	 */
	public ngOnDestroy(): void {
		if (this.isMarkedRead) {
			this.notificationsStore.prependOrPatchNotifications([
				{ ...this.notificationData, readAt: this.notificationService.getCurrentDateInUTC() }
			]);
		}
	}
}
